import React, { useEffect, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/South';
import KeyboardArrowUpIcon from '@mui/icons-material/North';
import PlusIcon from '@mui/icons-material/Add';

import styles from './MyReflexComposers.module.scss';
import { STATUS, VIDEOFEED } from '../../../../enums';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import useDebounce from '../../../../helpers/useDebounce';
import { getComposersThunk, saveQueryOptions, updateSortOption } from '../../../../store/composers/composersSlice';
import SearchInput from '../../../modules/SearchInput';
import Composer from '../../../MyReflexComposers/Composer';
import { PaginationFooter } from '../../../modules/PaginationFooter';
import { SortOptions, SortType } from '../../../../enums/SORT';
import LinearProgress from '../../../modules/LinearProgress';
import { ContainedButton } from '../../../modules/Buttons';

type TOnlineComposerModal = {
  onSelect: (id: string) => void;
  disableMyFoldersInSidebar: boolean;
  onCreateComposer: () => void;
  canCreateComposer?: boolean;
};

const OnlineComposerModal: React.FC<TOnlineComposerModal> = ({
  onSelect,
  disableMyFoldersInSidebar,
  onCreateComposer,
  canCreateComposer = true,
}) => {
  const dispatch = useAppDispatch();
  const { composers, status, page, limit, sort, total, sortOption } = useAppSelector((state) => state.composersStore);
  const [composersList, setComposersList] = useState<VIDEOFEED[]>([]);

  const [search, setSearch] = useState('');
  const debouncedValue = useDebounce<string>(search, 500);

  const fetchAllComposers = async () => {
    dispatch(getComposersThunk({ page, limit, search, sort, sortOption }));
  };

  useEffect(() => {
    if (composers) {
      fetchAllComposers();
    }
  }, [debouncedValue, page, limit, sort, sortOption]);

  useEffect(() => {
    if (composers) {
      setComposersList(composers);
    }
  }, [composers]);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onRowsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch(saveQueryOptions({ page: 0, limit: parseInt(e.target.value, 10), sort }));
  };

  const onPaginate = (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    dispatch(saveQueryOptions({ page: newPage, limit, sort }));
  };

  const onHeaderSort = (option: SortOptions) => {
    dispatch(updateSortOption(option));
    dispatch(saveQueryOptions({ page: 0, limit, sort: sort === SortType.ASC ? SortType.DESC : SortType.ASC }));
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.searchToolbar}>
        <SearchInput onChange={onSearch} />
        <ContainedButton className={styles.createComposerBtn} onClick={onCreateComposer} disabled={!canCreateComposer}>
          <PlusIcon fontSize="small" />
          Create New Composer
        </ContainedButton>
      </div>
      {status === STATUS.LOADING ? (
        <LinearProgress />
      ) : composersList.length > 0 ? (
        <div className={styles.composersList}>
          <div className={styles.toolbar}>
            <div className={styles.container}>
              <div className={styles.leftSide}>
                <div className={styles.name}>
                  <button type="button" onClick={() => onHeaderSort(SortOptions.NAME)} className={styles.btn}>
                    Name
                    {sortOption === SortOptions.NAME && sort === SortType.ASC ? <KeyboardArrowDownIcon className={styles.arrow} /> : null}
                    {sortOption === SortOptions.NAME && sort === SortType.DESC ? <KeyboardArrowUpIcon className={styles.arrow} /> : null}
                  </button>
                </div>
              </div>
              <div className={styles.rightSide}>
                <div className={styles.owner} onClick={() => onHeaderSort(SortOptions.OWNER)}>
                  Owner
                  {sortOption === SortOptions.OWNER && sort === SortType.DESC ? <KeyboardArrowDownIcon className={styles.arrow} /> : null}
                  {sortOption === SortOptions.OWNER && sort === SortType.ASC ? <KeyboardArrowUpIcon className={styles.arrow} /> : null}
                </div>
                <div className={styles.date} onClick={() => onHeaderSort(SortOptions.UPDATED_AT)}>
                  Last Modified
                  {sortOption === SortOptions.UPDATED_AT && sort === SortType.DESC ? (
                    <KeyboardArrowDownIcon className={styles.arrow} />
                  ) : null}
                  {sortOption === SortOptions.UPDATED_AT && sort === SortType.ASC ? <KeyboardArrowUpIcon className={styles.arrow} /> : null}
                </div>
                <div className={styles.size}>File Size</div>
              </div>
            </div>
          </div>
          <div className={styles.list}>
            {composersList.map((composer, i) => (
              <div key={composer.id} className="mb-5" onClick={() => onSelect(composer.id)}>
                <Composer composer={composer} feedIndex={i} noActions={true} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className={styles.noFoldersContainer}>
          <div className={styles.noFoldersText}>No composers found.</div>
        </div>
      )}
      <PaginationFooter page={page} total={total} onPageChange={onPaginate} rowsPerPage={limit} onRowsPerPageChange={onRowsChange} />
    </div>
  );
};

export default OnlineComposerModal;
